import * as THREE from 'three'

import { analyseAircraftGeometry } from './aircraftGeometry.js'

/**
 * Automatic alignment for a downloaded aircraft model.
 *
 * A model from a 3D asset site arrives in whatever units and orientation its
 * author happened to use: centimetres or metres, nose along +X or -Z, origin
 * at a wingtip or the centre of the bounding box. Lining it up by editing a
 * number, reloading, squinting, and repeating is genuinely miserable, and it
 * is the reason "just drop in a glTF" usually is not just.
 *
 * So this works it out from the geometry.
 *
 * THE ORIENTATION TRICK
 * The obvious approach — assume the longest axis is the fuselage — fails on a
 * Tomcat, whose 19.55 m span and 19.10 m length are within three per cent of
 * each other. The reliable signal is SYMMETRY. An aircraft is mirror-symmetric
 * about its centreline, so the distribution of vertices along the SPAN axis is
 * symmetric and its skewness is near zero, while along the LENGTH axis it is
 * strongly skewed (a thin nose at one end, engines and tails at the other).
 * Whichever horizontal axis is more skewed is the fuselage.
 *
 * Which end is the nose then follows from the same asymmetry: the tail end is
 * far taller, because that is where the fins are.
 */

/**
 * Vertex positions of every mesh, expressed in some ancestor's frame.
 *
 * Non-destructive, unlike sampleVertices below — it reads the matrices that
 * are already there rather than resetting the root to measure it. Used after
 * placement, when the question is where things ended up rather than what shape
 * the model is.
 */
export function sampleInFrame(model, frame, maxSamples = 20000) {
  frame.updateMatrixWorld(true)
  const inverse = new THREE.Matrix4().copy(frame.matrixWorld).invert()
  const meshes = []
  model.traverse((o) => {
    if (o.isMesh && o.geometry?.attributes?.position) meshes.push(o)
  })
  const total = meshes.reduce((n, m) => n + m.geometry.attributes.position.count, 0)
  const stride = Math.max(1, Math.ceil(total / maxSamples))

  const out = []
  const v = new THREE.Vector3()
  const toFrame = new THREE.Matrix4()
  for (const mesh of meshes) {
    toFrame.multiplyMatrices(inverse, mesh.matrixWorld)
    const pos = mesh.geometry.attributes.position
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(toFrame)
      out.push(v.x, v.y, v.z)
    }
  }
  return out
}

/** Vertex positions of every mesh, expressed in the model root's own frame. */
function sampleVertices(root, maxSamples = 20000) {
  root.position.set(0, 0, 0)
  root.rotation.set(0, 0, 0)
  root.scale.setScalar(1)
  root.updateMatrixWorld(true)

  const inverseRoot = new THREE.Matrix4().copy(root.matrixWorld).invert()
  const meshes = []
  root.traverse((o) => {
    if (o.isMesh && o.geometry?.attributes?.position) meshes.push(o)
  })

  const total = meshes.reduce((n, m) => n + m.geometry.attributes.position.count, 0)
  const stride = Math.max(1, Math.ceil(total / maxSamples))

  const out = []
  const v = new THREE.Vector3()
  const toRoot = new THREE.Matrix4()

  for (const mesh of meshes) {
    toRoot.multiplyMatrices(inverseRoot, mesh.matrixWorld)
    const pos = mesh.geometry.attributes.position
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(toRoot)
      out.push(v.x, v.y, v.z)
    }
  }
  return out
}

/**
 * @param {THREE.Object3D} root
 * @param {object} opts
 * @param {number} opts.trueLengthM   real-world length to scale to
 * @param {number} opts.noseZ         where the nose tip should end up, model frame
 * @param {number} [opts.verticalCentreY]
 * @returns {object} what it decided, for reporting
 */
export function autoFitAircraft(root, opts) {
  const { trueLengthM, verticalCentreY = 0.25, eyeZ, eyeFractionFromNose } = opts

  const samples = sampleVertices(root)
  const decision = analyseAircraftGeometry(samples, trueLengthM)
  if (!decision.ok) return decision

  root.rotation.set(0, (decision.yawDeg * Math.PI) / 180, 0)
  root.scale.setScalar(decision.scale)
  root.position.set(0, 0, 0)
  root.updateMatrixWorld(true)

  const box = new THREE.Box3().setFromObject(root)
  const centre = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())

  /**
   * Where to put the nose.
   *
   * Rather than a fixed datum, this is derived from where the PILOT ends up.
   * The design eye is fixed at z = -7.05 — the ball, the glideslope and the
   * grading are all measured from it and it must not move — so the model is
   * slid fore and aft until the eye sits the right fraction of the way back
   * from the nose. On a Tomcat that is about 23% — the FRONT cockpit. The 28%
   * a side view suggests is the bulkhead between the pilot and the RIO, and a
   * camera placed there is inside solid structure. See cockpitProbe.js, which
   * measures it rather than guessing.
   *
   * Deriving it this way means any model lands with the camera in the cockpit,
   * regardless of where its author put the origin. A fixed nose datum only
   * works for a model whose origin happens to match the one it was tuned for,
   * and this one's sits two metres aft of the procedural jet's.
   */
  const noseZ =
    eyeZ != null && eyeFractionFromNose != null
      ? eyeZ - eyeFractionFromNose * size.z
      : (opts.noseZ ?? -10.1)

  root.position.set(-centre.x, verticalCentreY - centre.y, noseZ - box.min.z)
  root.updateMatrixWorld(true)

  const finalBox = new THREE.Box3().setFromObject(root)

  return {
    ...decision,
    box: finalBox,
    noseZ,
    spanAfter: decision.spanBefore * decision.scale,
    heightAfter: size.y,
    /** Fraction of the way back from the nose the design eye ends up. */
    eyeFraction: eyeZ != null ? (eyeZ - finalBox.min.z) / Math.max(size.z, 1e-6) : null,
  }
}
